import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Select } from '@chakra-ui/react';
import Radio from 'components/radio/index';
import { FeeGroupSelection, FeeGroup, NewFeeGroupData, isFullFeeGroupData } from '../types';
import FeeGroupForm from './FeeGroupForm';

interface FeeGroupSelectorProps {
  value: FeeGroupSelection;
  onChange: (value: FeeGroupSelection) => void;
  disabled?: boolean;
  label?: string;
}

const FeeGroupSelector: React.FC<FeeGroupSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Fee Group',
}) => {
  const [feeGroupList, setFeeGroupList] = useState<FeeGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeeGroups();
  }, []);

  const fetchFeeGroups = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/feegroup/all-feegroups`
      );
      const data = response.data?.feegroups?.payload || [];
      setFeeGroupList(data.map((fg: any) => ({
        id: fg.feegroup_id,
        name: fg.description || fg.name,
        template: {
          fixed: fg.feetemplate?.fixed || '0',
          percentage: fg.feetemplate?.percentage || '0',
        },
      })));
    } catch (error) {
      console.error('Error fetching fee groups', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeChange = (mode: 'existing' | 'new') => {
    onChange({
      ...value,
      mode,
      existingFeeGroupId: mode === 'existing' ? value.existingFeeGroupId : undefined,
      existingFeeGroupName: mode === 'existing' ? value.existingFeeGroupName : undefined,
      newFeeGroup: mode === 'new' ? value.newFeeGroup || { name: '', description: '', fee_data: [] } : undefined,
    });
  };

  const handleExistingFeeGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selectedFeeGroup = feeGroupList.find(fg => fg.id === selectedId);
    onChange({
      ...value,
      existingFeeGroupId: selectedId,
      existingFeeGroupName: selectedFeeGroup?.name || '',
    });
  };

  const handleNewFeeGroupChange = (newFeeGroup: NewFeeGroupData) => {
    onChange({
      ...value,
      newFeeGroup,
    });
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-navy-700">
        {label} <span className="text-red-500">*</span>
      </label>

      {/* Mode Selection */}
      <div className="flex items-center gap-6">
        <label className="inline-flex items-center">
          <Radio
            id={`fee_mode_existing_${Math.random()}`}
            name="feeGroupMode"
            disabled={disabled}
            checked={value.mode === 'existing'}
            onChange={() => handleModeChange('existing')}
          />
          <span className="ml-2 text-sm text-navy-700">Select Existing</span>
        </label>
        <label className="inline-flex items-center">
          <Radio
            id={`fee_mode_new_${Math.random()}`}
            name="feeGroupMode"
            disabled={disabled}
            checked={value.mode === 'new'}
            onChange={() => handleModeChange('new')}
          />
          <span className="ml-2 text-sm text-navy-700">Create New</span>
        </label>
      </div>

      {/* Existing Fee Group Dropdown */}
      {value.mode === 'existing' && (
        <div>
          <Select
            disabled={disabled || isLoading}
            value={value.existingFeeGroupId || ''}
            onChange={handleExistingFeeGroupChange}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm shadow-sm focus:outline-none"
          >
            <option value="">
              {isLoading ? 'Loading...' : '-- Select Fee Group --'}
            </option>
            {feeGroupList.map(fg => (
              <option key={fg.id} value={fg.id}>
                {fg.name}
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* New Fee Group Form */}
      {value.mode === 'new' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <FeeGroupForm
            value={isFullFeeGroupData(value.newFeeGroup) ? value.newFeeGroup : { name: '', description: '', fee_data: [] }}
            onChange={handleNewFeeGroupChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default FeeGroupSelector;
